/**
 * useProjectConfig Hook
 * Manages project configuration including template-category definitions and project assignments.
 *
 * Storage strategy:
 * - Authenticated users: Projects are persisted to backend DynamoDB via API
 * - Guests: Projects are stored in localStorage only
 * - Template categories: Always stored locally (per-device preference)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type {
  ProjectConfiguration,
  TemplateCategoryConfig,
  ProjectConfig,
  SystemCategory,
} from '@/lib/types';
import { createLogger } from '@/lib/logger';
import { useAuthContext } from '@/contexts/AuthContext';
import { projectsApi } from '@/services/projectsApi';
import { settingsApi } from '@/services/settingsApi';
import { API_URL } from '@/lib/apiConfig';

const log = createLogger('useProjectConfig');

const STORAGE_KEY = 'systemdox_project_config';
const CURRENT_VERSION = 1;

// Default template-categories that map to system categories
const DEFAULT_TEMPLATE_CATEGORIES: TemplateCategoryConfig[] = [
  {
    id: 'tc-notes',
    templateId: null,
    templateName: 'Any Template',
    category: 'notes',
    displayName: 'Meeting Notes',
    description: 'General meeting notes and discussions',
    icon: 'FileText',
    color: 'blue',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tc-requirements',
    templateId: null,
    templateName: 'Any Template',
    category: 'requirements',
    displayName: 'Requirements',
    description: 'Product and technical requirements',
    icon: 'ClipboardList',
    color: 'amber',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'tc-architecture',
    templateId: null,
    templateName: 'Any Template',
    category: 'architecture',
    displayName: 'Architecture',
    description: 'System architecture and design documents',
    icon: 'GitBranch',
    color: 'emerald',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function createDefaultConfiguration(): ProjectConfiguration {
  return {
    version: CURRENT_VERSION,
    templateCategories: [...DEFAULT_TEMPLATE_CATEGORIES],
    projects: [],
    updatedAt: new Date().toISOString(),
  };
}

function loadConfiguration(): ProjectConfiguration | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const config = JSON.parse(stored) as ProjectConfiguration;
    log.debug('Loaded configuration', {
      templateCategories: config.templateCategories.length,
      projects: config.projects.length,
    });
    return config;
  } catch (err) {
    log.error('Failed to load configuration', err instanceof Error ? err : new Error(String(err)));
    return null;
  }
}

function saveConfiguration(config: ProjectConfiguration): void {
  try {
    const updated = {
      ...config,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    log.debug('Saved configuration');
  } catch (err) {
    log.error('Failed to save configuration', err instanceof Error ? err : new Error(String(err)));
    throw err;
  }
}

export interface UseProjectConfigReturn {
  // Data
  configuration: ProjectConfiguration | null;
  templateCategories: TemplateCategoryConfig[];
  projects: ProjectConfig[];
  isLoading: boolean;
  error: string | null;

  // Template-Category CRUD
  createTemplateCategory: (
    config: Omit<TemplateCategoryConfig, 'id' | 'createdAt' | 'updatedAt'>
  ) => TemplateCategoryConfig;
  updateTemplateCategory: (
    id: string,
    updates: Partial<Omit<TemplateCategoryConfig, 'id' | 'createdAt' | 'updatedAt'>>
  ) => TemplateCategoryConfig | null;
  deleteTemplateCategory: (id: string) => boolean;
  getTemplateCategoryById: (id: string) => TemplateCategoryConfig | undefined;

  // Project CRUD
  createProject: (
    project: Omit<ProjectConfig, 'id' | 'createdAt' | 'updatedAt' | 'templateIds'> & {
      templateIds?: string[];
    }
  ) => Promise<ProjectConfig>;
  updateProject: (
    id: string,
    updates: Partial<Omit<ProjectConfig, 'id' | 'createdAt' | 'updatedAt'>>
  ) => ProjectConfig | null;
  deleteProject: (id: string) => Promise<boolean>;
  archiveProject: (id: string) => boolean;
  getProjectById: (id: string) => ProjectConfig | undefined;

  // Assignment operations
  assignToProject: (projectId: string, templateCategoryId: string) => boolean;
  removeFromProject: (projectId: string, templateCategoryId: string) => boolean;
  getProjectTemplateCategories: (projectId: string) => TemplateCategoryConfig[];

  // Utilities
  refresh: () => void;
  initializeDefault: () => void;
  getTemplateCategoriesByCategory: (category: SystemCategory) => TemplateCategoryConfig[];
}

export function useProjectConfig(): UseProjectConfigReturn {
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const [configuration, setConfiguration] = useState<ProjectConfiguration | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load configuration on mount and auth state change
  useEffect(() => {
    if (authLoading) return;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let config = loadConfiguration() || createDefaultConfiguration();

        if (isAuthenticated && API_URL) {
          // For authenticated users, load projects from API (source of truth)
          try {
            const cloudProjects = await projectsApi.listProjects();
            log.info('Loaded projects from API', { count: cloudProjects.length });
            config = { ...config, projects: cloudProjects };
          } catch (err) {
            log.error('Failed to load projects from API, using local fallback', err instanceof Error ? err : new Error(String(err)));
          }

          // Load template categories from settings API (server wins over localStorage)
          try {
            const settings = await settingsApi.getUserSettings();
            if (settings?.templateCategories && Array.isArray(settings.templateCategories) && settings.templateCategories.length > 0) {
              config = { ...config, templateCategories: settings.templateCategories as TemplateCategoryConfig[] };
              log.info('Loaded template categories from API', { count: settings.templateCategories.length });
            }
          } catch {
            // Silently fall back to localStorage categories
          }

          // Keep template categories in localStorage, but not projects
          saveConfiguration({ ...config, projects: [] });
        }

        setConfiguration(config);
      } catch (err) {
        setError('Failed to load project configuration');
        log.error('Failed to load configuration', err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, authLoading]);

  // Sync template categories to settings API when they change (skip initial load)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!configuration || !isAuthenticated || !API_URL || isLoading) return;
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      return;
    }
    settingsApi.saveUserSettings({ templateCategories: configuration.templateCategories }).catch((err) => {
      log.warn('Failed to sync template categories to API');
    });
  }, [configuration?.templateCategories, isAuthenticated, isLoading]);

  // Derived data
  const templateCategories = useMemo(
    () => configuration?.templateCategories || [],
    [configuration]
  );

  const projects = useMemo(
    () => configuration?.projects.filter(p => !p.isArchived && p.projectName) || [],
    [configuration]
  );

  // Refresh projects from API without touching template categories
  const refresh = useCallback(async () => {
    if (!isAuthenticated || !API_URL) return;

    setIsLoading(true);
    setError(null);
    try {
      const cloudProjects = await projectsApi.listProjects();
      setConfiguration(prev => {
        const base = prev || loadConfiguration() || createDefaultConfiguration();
        return { ...base, projects: cloudProjects };
      });
    } catch (err) {
      log.warn('Failed to refresh projects from API');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Initialize with defaults if no configuration exists
  const initializeDefault = useCallback(() => {
    const defaultConfig = createDefaultConfiguration();
    saveConfiguration(defaultConfig);
    setConfiguration(defaultConfig);
    log.info('Initialized default configuration');
  }, []);

  // ============================================
  // Template-Category CRUD
  // ============================================

  const createTemplateCategory = useCallback(
    (config: Omit<TemplateCategoryConfig, 'id' | 'createdAt' | 'updatedAt'>): TemplateCategoryConfig => {
      const now = new Date().toISOString();
      const newTC: TemplateCategoryConfig = {
        ...config,
        id: `tc-${generateId()}`,
        createdAt: now,
        updatedAt: now,
      };

      setConfiguration(prev => {
        const baseConfig = prev || createDefaultConfiguration();
        const updated = {
          ...baseConfig,
          templateCategories: [...baseConfig.templateCategories, newTC],
        };
        saveConfiguration(updated);
        return updated;
      });

      log.info('Created template-category', { id: newTC.id, displayName: newTC.displayName });
      return newTC;
    },
    []
  );

  const updateTemplateCategory = useCallback(
    (
      id: string,
      updates: Partial<Omit<TemplateCategoryConfig, 'id' | 'createdAt' | 'updatedAt'>>
    ): TemplateCategoryConfig | null => {
      let updated: TemplateCategoryConfig | null = null;

      setConfiguration(prev => {
        if (!prev) return prev;

        const index = prev.templateCategories.findIndex(tc => tc.id === id);
        if (index === -1) return prev;

        const existing = prev.templateCategories[index];
        updated = {
          ...existing,
          ...updates,
          updatedAt: new Date().toISOString(),
        };

        const newConfig = {
          ...prev,
          templateCategories: [
            ...prev.templateCategories.slice(0, index),
            updated,
            ...prev.templateCategories.slice(index + 1),
          ],
        };
        saveConfiguration(newConfig);
        return newConfig;
      });

      if (updated) {
        log.info('Updated template-category', { id });
      }
      return updated;
    },
    []
  );

  const deleteTemplateCategory = useCallback((id: string): boolean => {
    let deleted = false;

    setConfiguration(prev => {
      if (!prev) return prev;

      const filtered = prev.templateCategories.filter(tc => tc.id !== id);
      if (filtered.length === prev.templateCategories.length) return prev;

      deleted = true;

      // Also remove from any project assignments
      const updatedProjects = prev.projects.map(p => ({
        ...p,
        templateIds: p.templateIds.filter(tcId => tcId !== id),
      }));

      const newConfig = {
        ...prev,
        templateCategories: filtered,
        projects: updatedProjects,
      };
      saveConfiguration(newConfig);
      return newConfig;
    });

    if (deleted) {
      log.info('Deleted template-category', { id });
    }
    return deleted;
  }, []);

  const getTemplateCategoryById = useCallback(
    (id: string): TemplateCategoryConfig | undefined => {
      return templateCategories.find(tc => tc.id === id);
    },
    [templateCategories]
  );

  const getTemplateCategoriesByCategory = useCallback(
    (category: SystemCategory): TemplateCategoryConfig[] => {
      return templateCategories.filter(tc => tc.category === category);
    },
    [templateCategories]
  );

  // ============================================
  // Project CRUD
  // For authenticated users, syncs to backend API
  // For guests, stores in localStorage only
  // ============================================

  const createProject = useCallback(
    async (
      project: Omit<ProjectConfig, 'id' | 'createdAt' | 'updatedAt' | 'templateIds'> & {
        templateIds?: string[];
      }
    ): Promise<ProjectConfig> => {
      const now = new Date().toISOString();
      const initialTemplateIds = project.templateIds || [];

      // Create optimistic local version
      const newProject: ProjectConfig = {
        ...project,
        id: `proj-${generateId()}`,
        templateIds: initialTemplateIds,
        createdAt: now,
        updatedAt: now,
        isArchived: false,
      };

      // Optimistically update local state
      setConfiguration(prev => {
        const baseConfig = prev || createDefaultConfiguration();
        const updated = {
          ...baseConfig,
          projects: [...baseConfig.projects, newProject],
        };
        // Only save to localStorage for guests
        if (!isAuthenticated) {
          saveConfiguration(updated);
        }
        return updated;
      });

      // For authenticated users, sync to API and return the server project
      if (isAuthenticated && API_URL) {
        try {
          const serverProject = await projectsApi.createProject({
            name: project.projectName,
            type: project.type,
            description: project.description,
            templateIds: initialTemplateIds,
          });
          // Replace optimistic project with server-generated version (which has the real ID and auto-assigned templates)
          setConfiguration(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              projects: prev.projects.map(p => p.id === newProject.id ? serverProject : p),
            };
          });
          log.info('Project synced to API', { projectId: serverProject.id });
          return serverProject;
        } catch (err) {
          log.error('Failed to sync project to API', err instanceof Error ? err : new Error(String(err)));
          // Revert on failure
          setConfiguration(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              projects: prev.projects.filter(p => p.id !== newProject.id),
            };
          });
          throw err;
        }
      }

      log.info('Created project', { id: newProject.id, projectName: newProject.projectName });
      return newProject;
    },
    [isAuthenticated]
  );

  const updateProject = useCallback(
    (
      id: string,
      updates: Partial<Omit<ProjectConfig, 'id' | 'createdAt' | 'updatedAt'>>
    ): ProjectConfig | null => {
      let updated: ProjectConfig | null = null;
      let originalProject: ProjectConfig | null = null;

      setConfiguration(prev => {
        if (!prev) return prev;

        const index = prev.projects.findIndex(p => p.id === id);
        if (index === -1) return prev;

        const existing = prev.projects[index];
        originalProject = existing;
        updated = {
          ...existing,
          ...updates,
          updatedAt: new Date().toISOString(),
        };

        const newConfig = {
          ...prev,
          projects: [
            ...prev.projects.slice(0, index),
            updated,
            ...prev.projects.slice(index + 1),
          ],
        };
        // Only save to localStorage for guests
        if (!isAuthenticated) {
          saveConfiguration(newConfig);
        }
        return newConfig;
      });

      // For authenticated users, sync to API
      if (isAuthenticated && API_URL && updated && originalProject) {
        const projectToRestore = originalProject;
        projectsApi.updateProject(id, {
          name: updates.projectName,
          type: updates.type,
          description: updates.description,
          isArchived: updates.isArchived,
          templateIds: updates.templateIds,
        }).catch(err => {
          log.error('Failed to sync project update to API', err instanceof Error ? err : new Error(String(err)));
          // Revert the optimistic update by restoring the original project
          setConfiguration(prev => {
            if (!prev) return prev;
            const index = prev.projects.findIndex(p => p.id === id);
            if (index === -1) return prev;
            return {
              ...prev,
              projects: [
                ...prev.projects.slice(0, index),
                projectToRestore,
                ...prev.projects.slice(index + 1),
              ],
            };
          });
        });
      }

      if (updated) {
        log.info('Updated project', { id });
      }
      return updated;
    },
    [isAuthenticated]
  );

  const deleteProject = useCallback(async (id: string): Promise<boolean> => {
    // For authenticated users, delete from API first, then update local state
    if (isAuthenticated && API_URL) {
      try {
        await projectsApi.deleteProject(id);
        log.info('Deleted project from API', { id });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        log.error('Failed to delete project from API', err instanceof Error ? err : new Error(message));
        setError(`Failed to delete project: ${message}`);
        return false;
      }
    }

    // Remove from local state (after DynamoDB succeeds for authenticated users)
    setConfiguration(prev => {
      if (!prev) return prev;
      const newConfig = {
        ...prev,
        projects: prev.projects.filter(p => p.id !== id),
      };
      if (!isAuthenticated) {
        saveConfiguration(newConfig);
      }
      return newConfig;
    });

    log.info('Deleted project', { id });
    return true;
  }, [isAuthenticated]);

  const archiveProject = useCallback((id: string): boolean => {
    let archived = false;

    setConfiguration(prev => {
      if (!prev) return prev;

      const index = prev.projects.findIndex(p => p.id === id);
      if (index === -1) return prev;

      archived = true;
      const updatedProject = {
        ...prev.projects[index],
        isArchived: true,
        updatedAt: new Date().toISOString(),
      };

      const newConfig = {
        ...prev,
        projects: [
          ...prev.projects.slice(0, index),
          updatedProject,
          ...prev.projects.slice(index + 1),
        ],
      };
      // Only save to localStorage for guests
      if (!isAuthenticated) {
        saveConfiguration(newConfig);
      }
      return newConfig;
    });

    // For authenticated users, sync to API
    if (isAuthenticated && API_URL && archived) {
      projectsApi.archiveProject(id).catch(err => {
        log.error('Failed to sync project archive to API', err instanceof Error ? err : new Error(String(err)));
      });
    }

    if (archived) {
      log.info('Archived project', { id });
    }
    return archived;
  }, [isAuthenticated]);

  const getProjectById = useCallback(
    (id: string): ProjectConfig | undefined => {
      return configuration?.projects.find(p => p.id === id);
    },
    [configuration]
  );

  // ============================================
  // Assignment Operations
  // ============================================

  const assignToProject = useCallback(
    (projectId: string, templateCategoryId: string): boolean => {
      let assigned = false;
      let newTemplateIds: string[] = [];

      setConfiguration(prev => {
        if (!prev) return prev;

        const projectIndex = prev.projects.findIndex(p => p.id === projectId);
        if (projectIndex === -1) return prev;

        const project = prev.projects[projectIndex];
        if (project.templateIds.includes(templateCategoryId)) return prev;

        assigned = true;
        newTemplateIds = [...project.templateIds, templateCategoryId];
        const updatedProject = {
          ...project,
          templateIds: newTemplateIds,
          updatedAt: new Date().toISOString(),
        };

        const newConfig = {
          ...prev,
          projects: [
            ...prev.projects.slice(0, projectIndex),
            updatedProject,
            ...prev.projects.slice(projectIndex + 1),
          ],
        };
        // Only save to localStorage for guests
        if (!isAuthenticated) {
          saveConfiguration(newConfig);
        }
        return newConfig;
      });

      // For authenticated users, sync to API (update project with new templateIds)
      if (isAuthenticated && API_URL && assigned) {
        projectsApi.updateProject(projectId, { templateIds: newTemplateIds }).catch(err => {
          log.error('Failed to sync template assignment', err instanceof Error ? err : new Error(String(err)));
        });
      }

      if (assigned) {
        log.info('Assigned template-category to project', { projectId, templateCategoryId });
      }
      return assigned;
    },
    [isAuthenticated]
  );

  const removeFromProject = useCallback(
    (projectId: string, templateCategoryId: string): boolean => {
      let removed = false;
      let newTemplateIds: string[] = [];

      setConfiguration(prev => {
        if (!prev) return prev;

        const projectIndex = prev.projects.findIndex(p => p.id === projectId);
        if (projectIndex === -1) return prev;

        const project = prev.projects[projectIndex];
        const filtered = project.templateIds.filter(id => id !== templateCategoryId);
        if (filtered.length === project.templateIds.length) return prev;

        removed = true;
        newTemplateIds = filtered;
        const updatedProject = {
          ...project,
          templateIds: filtered,
          updatedAt: new Date().toISOString(),
        };

        const newConfig = {
          ...prev,
          projects: [
            ...prev.projects.slice(0, projectIndex),
            updatedProject,
            ...prev.projects.slice(projectIndex + 1),
          ],
        };
        // Only save to localStorage for guests
        if (!isAuthenticated) {
          saveConfiguration(newConfig);
        }
        return newConfig;
      });

      // For authenticated users, sync to API (update project with new templateIds)
      if (isAuthenticated && API_URL && removed) {
        projectsApi.updateProject(projectId, { templateIds: newTemplateIds }).catch(err => {
          log.error('Failed to sync template removal', err instanceof Error ? err : new Error(String(err)));
        });
      }

      if (removed) {
        log.info('Removed template-category from project', { projectId, templateCategoryId });
      }
      return removed;
    },
    [isAuthenticated]
  );

  const getProjectTemplateCategories = useCallback(
    (projectId: string): TemplateCategoryConfig[] => {
      const project = configuration?.projects.find(p => p.id === projectId);
      if (!project) return [];

      return project.templateIds
        .map(id => templateCategories.find(tc => tc.id === id))
        .filter((tc): tc is TemplateCategoryConfig => tc !== undefined);
    },
    [configuration, templateCategories]
  );

  return {
    configuration,
    templateCategories,
    projects,
    isLoading,
    error,
    createTemplateCategory,
    updateTemplateCategory,
    deleteTemplateCategory,
    getTemplateCategoryById,
    createProject,
    updateProject,
    deleteProject,
    archiveProject,
    getProjectById,
    assignToProject,
    removeFromProject,
    getProjectTemplateCategories,
    refresh,
    initializeDefault,
    getTemplateCategoriesByCategory,
  };
}
